import { useState } from "react";
import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { JsonViewer } from "../base/JsonViewer";
import { SpanDetailTabs, TabDefinition } from "./SpanDetailTabs";
import { RawSpanDetailView, RawSpanData } from "./RawSpanDetailView";

// Input event structure
export interface InputEventData {
  event: {
    name: string;
    data: {
      source?: string;
      text?: string;
      language?: string | null;
      [key: string]: unknown;
    };
    timestamp?: string;
    turn?: number;
    background?: boolean;
    background_task_name?: string | null;
  };
  context: {
    agent_id?: string;
    environment?: string;
    session_id?: string;
    function_id?: string;
    configuration?: Record<string, unknown>;
    data?: Record<string, unknown>;
    history?: unknown[];
    conversation_history?: Array<{ role: string; content: string }>;
    cache?: Record<string, unknown>;
    testing?: Record<string, unknown>;
  };
}

// Output event structure (generic)
export interface OutputEvent {
  name: string;
  data: Record<string, unknown>;
  timestamp?: string;
  turn?: number;
  background?: boolean;
  background_task_name?: string | null;
}

const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  } catch {
    return timestamp;
  }
};

// Get icon and color for event type
const getEventStyle = (eventName: string): { icon: string; color: string; bgColor: string; borderColor: string } => {
  switch (eventName) {
    case "text_to_speech":
      return {
        icon: "volume-up",
        color: "text-blue-500 dark:text-blue-400",
        bgColor: "bg-blue-50 dark:bg-blue-900/20",
        borderColor: "border-blue-200 dark:border-blue-800",
      };
    case "log":
      return {
        icon: "message",
        color: "text-gray-500 dark:text-gray-400",
        bgColor: "bg-gray-50 dark:bg-gray-800",
        borderColor: "border-gray-200 dark:border-gray-700",
      };
    case "audio":
      return {
        icon: "music",
        color: "text-purple-500 dark:text-purple-400",
        bgColor: "bg-purple-50 dark:bg-purple-900/20",
        borderColor: "border-purple-200 dark:border-purple-800",
      };
    case "stop":
      return {
        icon: "stop",
        color: "text-red-500 dark:text-red-400",
        bgColor: "bg-red-50 dark:bg-red-900/20",
        borderColor: "border-red-200 dark:border-red-800",
      };
    default:
      return {
        icon: "code",
        color: "text-gray-500 dark:text-gray-400",
        bgColor: "bg-gray-50 dark:bg-gray-800",
        borderColor: "border-gray-200 dark:border-gray-700",
      };
  }
};

// Get summary text for an output event
const getEventSummary = (event: OutputEvent): string => {
  const data = event.data;
  switch (event.name) {
    case "text_to_speech":
      return (data.text as string) || "";
    case "log":
      const msg = (data.message as string) || "";
      // Truncate long messages
      return msg.length > 150 ? msg.substring(0, 150) + "..." : msg;
    case "audio":
      return (data.path as string) || "";
    case "stop":
      return (data.closing_speech as string) || "Session ended";
    default:
      return JSON.stringify(data).substring(0, 100);
  }
};

// Compact card for output events
const OutputEventCard = ({ event }: { event: OutputEvent }) => {
  const [expanded, setExpanded] = useState(false);
  const style = getEventStyle(event.name);
  const summary = getEventSummary(event);

  return (
    <View className={`rounded-lg border ${style.borderColor} ${style.bgColor} overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex flex-row items-start gap-2 p-2 text-left hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
      >
        <Icon
          name={style.icon}
          size={14}
          className={`${style.color} mt-0.5 flex-shrink-0`}
        />
        <View className="flex-1 min-w-0">
          <View className="flex flex-row items-center gap-2">
            <Text className={`text-xs font-medium ${style.color}`}>{event.name}</Text>
            {event.timestamp && <Text className="text-xs text-gray-400">{formatTimestamp(event.timestamp)}</Text>}
          </View>
          <Text className="text-sm text-gray-700 dark:text-gray-300 truncate">{summary}</Text>
        </View>
        <Icon
          name={expanded ? "chevron-up" : "chevron-down"}
          size={14}
          className="text-gray-400 flex-shrink-0 mt-0.5"
        />
      </button>
      {expanded && (
        <View className="border-t border-gray-200 dark:border-gray-700 p-2">
          <JsonViewer
            data={event.data}
            collapsed={3}
          />
        </View>
      )}
    </View>
  );
};

// Collapsible section component
const CollapsibleSection = ({
  title,
  icon,
  children,
  defaultOpen = false,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <View className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex flex-row items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <Icon
          name={icon}
          size={14}
          className="text-gray-500"
        />
        <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 flex-1 text-left">{title}</Text>
        <Icon
          name={open ? "chevron-up" : "chevron-down"}
          size={14}
          className="text-gray-400"
        />
      </button>
      {open && <View className="p-3 border-t border-gray-200 dark:border-gray-700">{children}</View>}
    </View>
  );
};

// Render input event based on type
const InputEventDisplay = ({ event }: { event: InputEventData["event"] }) => {
  const eventName = event.name;

  // Start event - show "Start" with icon and timestamp
  if (eventName === "start") {
    return (
      <View>
        <View className="flex flex-row items-center gap-2">
          <Icon
            name="play"
            size={20}
            className="text-emerald-500 dark:text-emerald-400"
          />
          <Text className="text-lg font-medium text-gray-800 dark:text-gray-200">Start</Text>
        </View>
        {event.timestamp && <Text className="text-xs text-gray-400 mt-1 ml-7">{formatTimestamp(event.timestamp)}</Text>}
      </View>
    );
  }

  // Timeout event - show "Timeout" with icon and timestamp
  if (eventName === "timeout") {
    return (
      <View>
        <View className="flex flex-row items-center gap-2">
          <Icon
            name="clock"
            size={20}
            className="text-amber-500 dark:text-amber-400"
          />
          <Text className="text-lg font-medium text-gray-800 dark:text-gray-200">Timeout</Text>
        </View>
        {event.timestamp && <Text className="text-xs text-gray-400 mt-1 ml-7">{formatTimestamp(event.timestamp)}</Text>}
      </View>
    );
  }

  // Text event - just show the text in green box
  if (eventName === "text" && event.data.text) {
    return (
      <View>
        <View className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 border border-emerald-200 dark:border-emerald-800">
          <Text className="text-sm text-gray-800 dark:text-gray-200">{event.data.text}</Text>
        </View>
        {event.timestamp && <Text className="text-xs text-gray-400 mt-1">{formatTimestamp(event.timestamp)}</Text>}
      </View>
    );
  }

  // Stop event - show "Stop" with icon and optional closing speech
  if (eventName === "stop") {
    const closingSpeech = event.data.closing_speech as string | null | undefined;
    return (
      <View>
        <View className="flex flex-row items-center gap-2">
          <Icon
            name="stop"
            size={20}
            className="text-red-500 dark:text-red-400"
          />
          <Text className="text-lg font-medium text-gray-800 dark:text-gray-200">Stop</Text>
        </View>
        {event.timestamp && <Text className="text-xs text-gray-400 mt-1 ml-7">{formatTimestamp(event.timestamp)}</Text>}
        {closingSpeech && (
          <View className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800 mt-3">
            <Text className="text-sm text-gray-800 dark:text-gray-200">{closingSpeech}</Text>
          </View>
        )}
      </View>
    );
  }

  // Fallback for other event types
  return (
    <View>
      <View className="flex flex-row items-center gap-2">
        <Icon
          name="input"
          size={20}
          className="text-emerald-500 dark:text-emerald-400"
        />
        <Text className="text-lg font-medium text-gray-800 dark:text-gray-200">{eventName}</Text>
      </View>
      {event.timestamp && <Text className="text-xs text-gray-400 mt-1 ml-7">{formatTimestamp(event.timestamp)}</Text>}
    </View>
  );
};

// Content component for the Input Event tab
const InputEventContent = ({ inputData, outputEvents }: { inputData: InputEventData; outputEvents: OutputEvent[] }) => {
  const event = inputData.event;
  const context = inputData.context;

  return (
    <View className="space-y-6">
      {/* Input Event Display */}
      <InputEventDisplay event={event} />

      {/* Context (collapsible) */}
      <CollapsibleSection
        title="Context"
        icon="code"
      >
        <JsonViewer
          data={context}
          collapsed={10}
        />
      </CollapsibleSection>

      {/* Output Events */}
      {outputEvents.length > 0 && (
        <View>
          <View className="flex flex-row items-center gap-2 mb-2">
            <Icon
              name="data-array"
              size={14}
              className="text-indigo-500 dark:text-indigo-400"
            />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Outputs ({outputEvents.length})</Text>
          </View>
          <View className="space-y-2">
            {outputEvents.map((outputEvent, index) => (
              <OutputEventCard
                key={index}
                event={outputEvent}
              />
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

// Main InputEventSpanDetailView component with tabs
interface InputEventSpanDetailViewProps {
  inputData: InputEventData;
  outputEvents: OutputEvent[];
  rawData: RawSpanData;
  className?: string;
}

const tabs: TabDefinition[] = [
  { id: "event", label: "Event", color: "emerald" },
  { id: "attributes", label: "Attributes", color: "gray" },
];

export const InputEventSpanDetailView = ({ inputData, outputEvents, rawData, className }: InputEventSpanDetailViewProps) => {
  const [activeTab, setActiveTab] = useState("event");

  return (
    <View className={`flex flex-col overflow-hidden h-full ${className || ""}`}>
      <SpanDetailTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <View className="flex-1 overflow-y-auto p-4">
        {activeTab === "event" ? (
          <InputEventContent
            inputData={inputData}
            outputEvents={outputEvents}
          />
        ) : (
          <RawSpanDetailView data={rawData} />
        )}
      </View>
    </View>
  );
};

// Helper to parse input event data from span attributes
export const parseInputEventData = (attributes: Record<string, unknown>): { inputData: InputEventData; outputEvents: OutputEvent[] } | null => {
  const inputValue = attributes["input.value"];
  const outputValue = attributes["output.value"];

  if (typeof inputValue !== "string") return null;

  try {
    const inputData = JSON.parse(inputValue) as InputEventData;

    let outputEvents: OutputEvent[] = [];
    if (typeof outputValue === "string") {
      try {
        outputEvents = JSON.parse(outputValue) as OutputEvent[];
      } catch {
        // Output may not be valid JSON array
      }
    }

    return { inputData, outputEvents };
  } catch {
    return null;
  }
};

// Check if this is an input event span
export const isInputEventSpan = (attributes: Record<string, unknown>): boolean => {
  const eventName = attributes["voicerun.event.name"];
  const spanKind = attributes["openinference.span.kind"];
  return spanKind === "chain" && typeof eventName === "string" && eventName.length > 0;
};
